import React from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap } from 'lucide-react';
import Particles from '@/lib/components/ui/special/Particles';
import { ContainerTextFlip } from '@/components/text/ContainerTextFlip';
import { Spotlight } from '@/components/ui/Spotlight';

const Home: React.FC = () => {
  const navigate = useNavigate();

  const handleLogin = () => {
    navigate('/login');
  };

  const handleGetStarted = () => {
    navigate('/login');
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/assets/landing_3.jpg)',
        }}
      />

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Spotlight Effect */}
      <Spotlight className="absolute -top-40 left-0 md:-top-20 md:left-60" fill="emerald" />

      {/* Particles Effect */}
      <Particles className="absolute inset-0" quantity={80} ease={70} color="#10b981" size={0.8} staticity={30} />

      {/* Content */}
      <div className="relative z-10 flex min-h-screen flex-col">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center justify-between p-8"
        >
          {/* Company Name */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h1
              className="text-4xl font-bold tracking-tight text-white"
              style={{ fontFamily: '"Space Grotesk", sans-serif' }}
            >
              Enexan
            </h1>
          </motion.div>

          {/* Login Button */}
          <motion.button
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            whileHover={{ scale: 1.05, y: -1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleLogin}
            className="group relative overflow-hidden rounded-2xl border border-emerald-400/30 bg-white/10 px-6 py-3 font-medium text-white backdrop-blur-sm transition-all duration-300 hover:border-emerald-400/50 hover:bg-emerald-500/10 hover:shadow-lg hover:shadow-emerald-500/25"
          >
            <span className="relative z-10 flex items-center gap-2">
              Συνδεθείτε
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
            {/* Button shine effect */}
            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-emerald-400/10 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
          </motion.button>
        </motion.header>

        {/* Main Content */}
        <div className="flex flex-1 items-end justify-between p-8">
          {/* Left Side - Copywriting */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="max-w-2xl"
          >
            {/* Tag */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="mb-6 inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-500/20 px-4 py-2 shadow-lg shadow-emerald-500/20 backdrop-blur-sm"
            >
              <Zap className="mr-2 h-4 w-4 animate-pulse text-emerald-400" />
              <span className="text-sm font-medium text-emerald-300">Λύσεις Καθαρής Ενέργειας</span>
            </motion.div>

            {/* Title with ContainerTextFlip */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="mb-6"
            >
              <h2 className="mb-4 text-5xl leading-tight font-bold text-white lg:text-6xl">Τροφοδότησε το Μέλλον</h2>
              <div className="flex items-center justify-start">
                <ContainerTextFlip
                  words={['Βιώσιμα', 'Έξυπνα', 'Καθαρά', 'Πράσινα']}
                  interval={2500}
                  className="!text-3xl lg:!text-5xl"
                />
              </div>
            </motion.div>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.4 }}
              className="mb-8 max-w-xl text-lg leading-relaxed text-gray-200 lg:text-xl"
            >
              Μεταμορφώστε την κατανάλωση ενέργειάς σας με έξυπνη παρακολούθηση και ανανεώσιμες πηγές. Μειώστε το
              αποτύπωμα άνθρακα και εξοικονομήστε κόστος.
            </motion.p>
          </motion.div>

          {/* Right Side - Get Started Button */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.6 }}
            className="flex flex-col items-end"
          >
            {/* Get Started Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.8 }}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGetStarted}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 px-8 py-4 font-semibold text-white shadow-2xl shadow-emerald-500/25 transition-all duration-300 hover:shadow-emerald-500/40"
            >
              <span className="relative z-10 flex items-center gap-2 text-lg">
                Ξεκινήστε εδώ
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </span>

              {/* Button shine effect */}
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
            </motion.button>

            {/* Call to action text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 2 }}
              className="mt-4 text-sm text-gray-400"
            >
              Ξεκινήστε τη μεταμόρφωση της ενέργειάς σας σήμερα
            </motion.p>
          </motion.div>
        </div>

        {/* Bottom Indicator */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 2.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 shadow-lg shadow-emerald-500/20 backdrop-blur-sm">
            <div className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
            <span className="text-xs font-medium text-emerald-200">Τροφοδοτείται από ανανεώσιμη ενέργεια</span>
          </div>
        </motion.div> */}
      </div>
    </div>
  );
};

export default Home;
